import { createPortal } from 'react-dom';
import { useEffect, type CSSProperties } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { useShallow } from 'zustand/react/shallow';
import { CheckCircle, Info } from 'lucide-react';

import { useToastStore } from '@/shared/store/toastStore';

const colors: Record<string, string> = {
  success: '#4AC100',
  error: '#E0191A',
};

export default function GlobalToastAlert() {
  const { type, message, isVisible, bottom, hideToast } = useToastStore(
    useShallow((state) => ({
      type: state.type,
      message: state.message,
      isVisible: state.isVisible,
      bottom: state.bottom,
      hideToast: state.hideToast,
    }))
  );

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        hideToast();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, hideToast]);

  const alertVariants = {
    hidden: { x: '110%' },
    enter: { x: '-10%', transition: { type: 'tween', duration: 0.3 } },
    exit: { x: '110%', transition: { type: 'tween', duration: 0.3 } },
  } as const;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <S.ToastAlertContainer variants={alertVariants} initial='hidden' animate='enter' exit='exit' $bottom={bottom} $type={type}>
          <i className='flex h-5 w-5 shrink-0 items-center justify-center'>
            {type === 'success' ? (
              <CheckCircle style={{ color: 'white' } as CSSProperties} size={20} />
            ) : (
              <Info style={{ color: 'white' } as CSSProperties} size={20} />
            )}
          </i>
          <S.Message>{message}</S.Message>
        </S.ToastAlertContainer>
      )}
    </AnimatePresence>,
    document.body
  );
}

const S = {
  ToastAlertContainer: styled(motion.div)<{ $bottom: string; $type: string }>`
    display: flex;
    align-items: center;
    gap: 0.75rem;

    position: fixed;
    bottom: ${({ $bottom }) => $bottom};
    right: 0;

    width: 32.5rem;
    padding: 1rem;
    border-radius: 0.5rem;
    background-color: ${({ $type }) => colors[$type]};

    box-shadow: 0px 8px 30px 0px rgba(0, 0, 0, 0.16);
    z-index: 9999;
  `,

  Message: styled.p`
    color: white;
    font-size: 1.125rem;
    font-weight: 500;
    font-family: 'Pretendard';
  `,
};
